// What the depth reader reads (ios DepthDocument.swift): an article is one chapter of source paragraphs; skipped
// (filler) paragraphs are kept and drawn dimmed, quote paragraphs styled as quotes, images anchored before paragraphs.
import { sourceParagraphs, title, type Article, displaySource } from "../model/article";
import type { ArticleImage } from "../model/images";

export interface DocParagraph { kind: "body" | "quote" | "heading"; text: string; dimmed: boolean; skipReason?: string }
export interface DocChapter { title: string; paragraphs: DocParagraph[]; images: ArticleImage[] }
export interface DepthDocument { key: string; itemID: string; title: string; author: string; siteName: string; dek: string | null; isBook: boolean; chapters: DocChapter[] }

export const kicker = (d: DepthDocument) => [d.siteName, d.author].filter(Boolean).join(" · ") || "Article";

const cache = new Map<string, { sig: string; doc: DepthDocument }>();
export const documentsByKey = new Map<string, DepthDocument>();

export function documentFor(a: Article): DepthDocument | null {
  const paragraphs = sourceParagraphs(a);
  if (!paragraphs.length) return null;
  const sig = `${(a.meta.text ?? a.meta.sharedText ?? "").length}|${(a.meta.skipped ?? []).length}|${a.meta.images?.length ?? -1}|${a.meta.title}`;
  const hit = cache.get(a.id);
  if (hit && hit.sig === sig) return hit.doc;
  const quotes = new Set(a.meta.quoteParagraphs ?? []);
  const skipped = a.meta.skipped ?? [];
  const reasons = a.meta.skipReasons ?? [];
  const skipSet = new Set(skipped);
  const doc: DepthDocument = {
    key: a.id, itemID: a.id, title: title(a), author: a.meta.author || "", siteName: a.meta.siteName || displaySource(a),
    dek: a.meta.excerpt || null, isBook: false,
    chapters: [{ title: title(a), images: a.meta.images ?? [], paragraphs: paragraphs.map((text, i) => ({
      kind: quotes.has(i) ? "quote" : "body", text, dimmed: skipSet.has(i),
      skipReason: skipSet.has(i) ? reasons[skipped.indexOf(i)] : undefined,
    })) }],
  };
  cache.set(a.id, { sig, doc });
  documentsByKey.set(doc.key, doc);
  return doc;
}
